// pages/OpenLeaks/components/OpenLeaksTimeline/OpenLeaksTimeline.tsx (with virtual scrolling - FIXED)
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { DataLeak } from "features/openleak";
import TimelineLeakCard from '../TimelineLeakCard/TimelineLeakCard';
import './OpenLeaksTimeline.scss';

interface OpenLeaksTimelineProps {
  leaks: DataLeak[];
  loading?: boolean;
}

// Height of each timeline event in pixels (for virtual scrolling)
const EVENT_HEIGHT = 380;
const OVERSCAN_COUNT = 3; // Number of items to render above/below viewport

const OpenLeaksTimeline: React.FC<OpenLeaksTimelineProps> = ({ leaks, loading = false }) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
  const [containerHeight, setContainerHeight] = useState(0);
  const [lineHeight, setLineHeight] = useState(0); // ← MISSING STATE DECLARATION
  const dotsRef = useRef<(HTMLDivElement | null)[]>([]);
  const visibleDotsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Sort leaks by date (oldest first for timeline flow)

  // Calculate total height of all events
  const totalHeight = leaks.length * EVENT_HEIGHT;

  // Calculate visible items based on scroll position
  const updateVisibleRange = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const scrollTop = scrollContainerRef.current.scrollTop;
    const viewportHeight = scrollContainerRef.current.clientHeight;

    const startIndex = Math.max(0, Math.floor(scrollTop / EVENT_HEIGHT) - OVERSCAN_COUNT);
    const endIndex = Math.min(
      leaks.length,
      Math.ceil((scrollTop + viewportHeight) / EVENT_HEIGHT) + OVERSCAN_COUNT
    );

    setVisibleRange({ start: startIndex, end: endIndex });
    setContainerHeight(scrollContainerRef.current.clientHeight);
  }, [leaks.length]);

  // Handle scroll events with throttling for performance
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const throttledUpdate = () => {
      requestAnimationFrame(updateVisibleRange);
    };

    scrollContainer.addEventListener('scroll', throttledUpdate);
    window.addEventListener('resize', throttledUpdate);

    // Initial calculation
    updateVisibleRange();

    return () => {
      scrollContainer.removeEventListener('scroll', throttledUpdate);
      window.removeEventListener('resize', throttledUpdate);
    };
  }, [updateVisibleRange]);

  // Calculate line height based on visible dots positions
  useEffect(() => {
    if (!timelineRef.current || visibleDotsRef.current.length === 0) return;

    const calculateLineHeight = () => {
      const timelineRect = timelineRef.current?.getBoundingClientRect();
      if (!timelineRect) return;

      const visibleDots = visibleDotsRef.current.filter(dot => dot !== null);
      if (visibleDots.length === 0) return;

      const firstDot = visibleDots[0];
      const lastDot = visibleDots[visibleDots.length - 1];

      if (firstDot && lastDot) {
        const firstRect = firstDot.getBoundingClientRect();
        const lastRect = lastDot.getBoundingClientRect();

        const startY = firstRect.top - timelineRect.top + 8;
        const endY = lastRect.bottom - timelineRect.top - 8;

        setLineHeight(endY - startY);
      }
    };

    calculateLineHeight();
    window.addEventListener('resize', calculateLineHeight);

    return () => window.removeEventListener('resize', calculateLineHeight);
  }, [visibleRange, leaks]);

  // Scroll animation - draw line progressively (optimized for virtual scroll)
  useEffect(() => {
    const line = lineRef.current;
    if (!line) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = target.getAttribute('data-index');
            if (index) {
              target.classList.add('timeline-event-visible');

              // Update line height progressively
              const dotIndex = parseInt(index);
              const dotElement = visibleDotsRef.current[dotIndex - visibleRange.start];
              if (lineRef.current && dotElement) {
                const dotRect = dotElement.getBoundingClientRect();
                const timelineRect = timelineRef.current?.getBoundingClientRect();
                if (dotRect && timelineRect) {
                  const newHeight = dotRect.bottom - timelineRect.top - 8;
                  lineRef.current.style.height = `${newHeight}px`;
                }
              }
            }
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -50px 0px' }
    );

    // Observe only visible events
    const visibleEvents = document.querySelectorAll('.timeline-event[data-index]');
    visibleEvents.forEach((event) => {
      observer.observe(event);
    });

    return () => observer.disconnect();
  }, [visibleRange]);

  // Reset visible dots ref when range changes
  useEffect(() => {
    visibleDotsRef.current = [];
  }, [visibleRange]);

  // Get visible leaks
  const visibleLeaks = useMemo(() => {
    return leaks.slice(visibleRange.start, visibleRange.end);
  }, [leaks, visibleRange]);

  // Reset scroll position when leaks change
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
      updateVisibleRange();
    }
  }, [leaks, updateVisibleRange]);

  if (!leaks || leaks.length === 0) {
    return (
      <div className="timeline-empty-container">
        <div className="timeline-empty-message">
          <svg className="timeline-empty-icon" width="64" height="64" viewBox="0 0 24 24" fill="none">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#666" strokeWidth="2"/>
            <path d="M12 8V12" stroke="#666" strokeWidth="2"/>
            <path d="M12 16H12.01" stroke="#666" strokeWidth="2"/>
          </svg>
          <h3>Aucune fuite détectée</h3>
          <p>Aucune fuite de données n'a été trouvée pour le moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="openleaks-timeline" ref={timelineRef}>
      {/* Center line with pulse animation */}
      <div
        ref={lineRef}
        className="timeline-center-line"
        style={{ height: lineHeight ? `${lineHeight}px` : '0px' }}
      ></div>

      {/* Virtual scroll container */}
      <div
        ref={scrollContainerRef}
        className="timeline-scroll-container"
        onScroll={updateVisibleRange}
      >
        {/* Spacer for total height */}
        <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
          {/* Render only visible items */}
          <div
            className="timeline-events-container"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              transform: `translateY(${visibleRange.start * EVENT_HEIGHT}px)`
            }}
          >
            {visibleLeaks.map((leak, idx) => {
              const globalIndex = visibleRange.start + idx;
              const side = globalIndex % 2 === 0 ? 'right' : 'left';

              return (
                <div
                  key={leak.id}
                  className="timeline-event timeline-event-hidden"
                  data-index={globalIndex}
                  style={{ height: `${EVENT_HEIGHT}px` }}
                >
                  {/* Connection dot with severity color */}
                  <div
                    ref={el => {
                      visibleDotsRef.current[idx] = el;
                      dotsRef.current[globalIndex] = el;
                    }}
                    className="timeline-connection-dot"
                    style={{
                      backgroundColor: leak.severity
                        ? (leak.severity === 'critical' ? '#EF4444' :
                          leak.severity === 'high' ? '#F97316' :
                            leak.severity === 'medium' ? '#F59E0B' : '#10B981')
                        : '#9CA3AF'
                    }}
                  >
                    <div className="dot-pulse"></div>
                  </div>

                  {/* Date marker */}
                  <div className="timeline-date-marker">
                    {new Date(leak.BreachDate).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }).replace('.', '')}
                  </div>

                  {/* Card */}
                  <TimelineLeakCard leak={leak} side={side} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Loading indicator for more items */}
      {loading && (
        <div className="timeline-loading-more">
          <div className="loader-small"></div>
          <span>Chargement...</span>
        </div>
      )}
    </div>
  );
};

export default OpenLeaksTimeline;